import type { GenerationRecentItem } from '@/api/generation';
import { getRecent } from '@/api/generation';

interface RecentUsageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (item: GenerationRecentItem) => void;
}

export default function RecentUsageModal({ isOpen, onClose, onSelect }: RecentUsageModalProps) {
  const { t } = useTranslation();
  const [items, setItems] = useState<GenerationRecentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError(false);
    getRecent()
      .then((data) => setItems(data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full bg-white rounded-t-3xl max-h-[70vh] overflow-hidden animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <h3 className="text-lg font-bold text-gray-800">{t('gen_recentTitle')}</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>

        <div className="px-5 pb-8 overflow-y-auto max-h-[calc(70vh-64px)]">
          {/* Hint */}
          <p className="text-xs text-gray-400 mb-3">{t('gen_clickToRefill')}</p>

          {/* Loading skeleton */}
          {loading && (
            <div className="flex flex-col gap-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-2xl bg-gray-50 animate-pulse">
                  <div className="w-12 h-12 rounded-xl bg-gray-200 flex-shrink-0" />
                  <div className="flex-1 flex flex-col gap-1.5">
                    <div className="h-3 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-200 rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Error / empty */}
          {!loading && (error || items.length === 0) && (
            <div className="flex flex-col items-center gap-2 py-10">
              <i className="ri-history-line text-gray-300 text-4xl"></i>
              <p className="text-sm text-gray-400">{t('gen_noRecent')}</p>
            </div>
          )}

          {/* List */}
          {!loading && !error && items.length > 0 && (
            <div className="flex flex-col gap-2">
              {items.map((item) => (
                <button
                  key={item.gen_id}
                  onClick={() => { onSelect(item); onClose(); }}
                  className="flex items-center gap-3 p-3 rounded-2xl text-left cursor-pointer transition-all active:scale-95 hover:bg-gray-50"
                  style={{ border: '1px solid #f3f4f6' }}
                >
                  {/* Thumbnail */}
                  <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                    {item.thumbnail_url || item.image_url ? (
                      <img
                        src={item.thumbnail_url ?? item.image_url}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const el = e.currentTarget;
                          el.style.display = 'none';
                          if (el.parentElement) {
                            el.parentElement.style.background = 'linear-gradient(135deg, #FF6B9D, #A855F7)';
                          }
                        }}
                      />
                    ) : (
                      <div
                        className="w-full h-full"
                        style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
                      />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{item.prompt}</p>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <span
                        className="px-1.5 py-0.5 rounded text-xs font-medium text-white"
                        style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)', fontSize: '10px' }}
                      >
                        {item.model_id}
                      </span>
                      <span
                        className="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500"
                        style={{ fontSize: '10px' }}
                      >
                        {item.quality}
                      </span>
                      {item.ratio && (
                        <span
                          className="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500"
                          style={{ fontSize: '10px' }}
                        >
                          {item.ratio}
                        </span>
                      )}
                    </div>
                  </div>

                  <i className="ri-arrow-right-s-line text-gray-300 flex-shrink-0"></i>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
